import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { normaliseBooksResponse, CoverArt } from "../utils/books";

function CollectionDetail() {
  const { id }   = useParams();
  const userId   = localStorage.getItem("userId");

  const [col,       setCol]       = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [notFound,  setNotFound]  = useState(false);
  const [isAuthor,  setIsAuthor]  = useState(false);
  const [editing,   setEditing]   = useState(false);
  const [form,      setForm]      = useState({ title: '', description: '' });
  const [saving,    setSaving]    = useState(false);
  const [copied,    setCopied]    = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [myBooks,   setMyBooks]   = useState([]);
  const [bookSearch, setBookSearch] = useState('');

  useEffect(() => {
    axios.get(`/api/collections/${id}`)
      .then(({ data }) => {
        setCol(data.collection);
        setForm({ title: data.collection.title, description: data.collection.description || '' });
        setIsAuthor(!!userId && userId === data.collection.userId);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, userId]);

  useEffect(() => {
    if (!isAuthor || !showPicker) return;
    axios.get(`/api/books/${userId}`)
      .then(r => setMyBooks(normaliseBooksResponse(r.data)))
      .catch(() => {});
  }, [isAuthor, showPicker, userId]);

  const saveEdits = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post('/api/collections/update', { id, userId, ...form });
      setCol(data.collection);
      setEditing(false);
    } finally { setSaving(false); }
  };

  const addBook = async (book) => {
    const { data } = await axios.post('/api/collections/addbook', {
      id, userId,
      book: { bookId: book._id, title: book.title, author: book.author || '', imageLink: book.imageLink || '' },
    });
    setCol(data.collection);
  };

  const removeBook = async (bookId) => {
    const { data } = await axios.post('/api/collections/removebook', { id, userId, bookId });
    setCol(data.collection);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const colBookIds = new Set(col?.books.map(b => b.bookId).filter(Boolean));
  const filteredMyBooks = myBooks.filter(b =>
    !colBookIds.has(b._id) &&
    (!bookSearch || b.title.toLowerCase().includes(bookSearch.toLowerCase()) ||
      b.author?.toLowerCase().includes(bookSearch.toLowerCase()))
  );

  if (loading) return (
    <div className="min-h-screen bg-ink-base flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-ink-base flex flex-col items-center justify-center gap-4">
      <p className="text-cream font-semibold">Collection not found</p>
      <Link to="/" className="sc-btn-ghost text-sm">Go home</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-ink-base">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            {editing ? (
              <div className="flex-1 space-y-3">
                <input className="sc-input text-lg font-semibold" value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} autoFocus />
                <textarea className="sc-input resize-none text-sm" rows={2} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description (optional)" />
                <div className="flex gap-2">
                  <button onClick={saveEdits} disabled={saving}
                    className="sc-btn-gold text-xs py-1.5 px-4 disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setEditing(false)} className="sc-btn-ghost text-xs py-1.5 px-4">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-cream tracking-tight">{col.title}</h1>
                {col.description && (
                  <p className="text-cream-muted text-sm mt-1.5 leading-relaxed">{col.description}</p>
                )}
                <p className="text-cream-muted text-xs mt-2">
                  {col.books.length} book{col.books.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {!editing && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={copyLink}
                  className="sc-btn-ghost text-xs py-1.5 px-3">
                  {copied ? 'Copied!' : 'Share'}
                </button>
                {isAuthor && (
                  <button onClick={() => setEditing(true)}
                    className="sc-btn-ghost text-xs py-1.5 px-3">
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add from library */}
        {isAuthor && (
          <div className="mb-6">
            <button onClick={() => { setShowPicker(o => !o); setBookSearch(''); }}
              className="sc-btn-ghost text-xs py-1.5 px-4">
              {showPicker ? 'Close' : '+ Add from library'}
            </button>

            {showPicker && (
              <div className="mt-3 bg-ink-surface border border-ink-border rounded-xl overflow-hidden">
                <div className="p-3 border-b border-ink-border">
                  <input className="sc-input text-sm" value={bookSearch}
                    onChange={e => setBookSearch(e.target.value)}
                    placeholder="Search your library…" autoFocus />
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {filteredMyBooks.length === 0 ? (
                    <p className="text-cream-muted text-sm text-center py-6">
                      {bookSearch ? 'No matches' : 'All your books are already in this collection'}
                    </p>
                  ) : filteredMyBooks.map((book, i) => (
                    <button key={book._id || i}
                      onClick={() => addBook(book)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-ink-raised transition-colors text-left border-b border-ink-border last:border-0">
                      <div className="w-7 h-10 rounded overflow-hidden flex-shrink-0">
                        <CoverArt book={book} className="w-full h-full" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-cream text-xs font-medium line-clamp-1">{book.title}</p>
                        <p className="text-cream-muted text-[11px]">{book.author}</p>
                      </div>
                      <span className="text-gold text-xs flex-shrink-0">Add →</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Books */}
        {col.books.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-cream font-semibold mb-1">No books yet</p>
            {isAuthor && (
              <p className="text-cream-muted text-sm">Add books from your library above.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {col.books.map((book, i) => (
              <div key={book._id || i}
                className="flex items-center gap-4 bg-ink-surface border border-ink-border rounded-xl p-4">
                <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0">
                  {book.imageLink
                    ? <img src={book.imageLink} alt={book.title} className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-ink-raised flex items-center justify-center">
                        <span className="text-cream-muted text-xs font-bold">{(book.title || '?')[0]}</span>
                      </div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-cream text-sm font-semibold line-clamp-1">{book.title}</p>
                  <p className="text-cream-muted text-xs">{book.author}</p>
                </div>
                {isAuthor && (
                  <button onClick={() => removeBook(book._id?.toString() || book.bookId)}
                    className="text-xs text-cream-muted hover:text-crimson transition-colors flex-shrink-0 px-1">
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Back link for non-authors */}
        {!isAuthor && (
          <div className="mt-10 text-center">
            <Link to="/" className="text-xs text-cream-muted hover:text-cream transition-colors">
              ← StoryChats
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default CollectionDetail;
